import React, { useState, useEffect } from "react";
import absoluteUrl from "next-absolute-url";
import { stripHtml } from "string-strip-html";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { Trans, useTranslation } from "next-i18next";
import { OGMetaDataHead, Items, Layout } from "components";
import AuthService from "services/auth";
import StoreService from "services/store";
import { IS_THUMBNAILER_ENABELED, THUMBNAILER_URL } from "config/constants";
import { asyncAwaitWrap, splitBySepratorAndCreateAnArray } from "utils/helpers";
import { generateSubdomainFromLocale } from "utils/app";
import { setDefaultAuthHeader, setUserLang } from "utils/axios";
import "swiper/css";
import "swiper/css/pagination";

const Product = ({ store, item, origin }) => {
  const router = useRouter();
  const locale = router?.locale ?? "en-AE";
  const { t: trans } = useTranslation("common");
  const whatsapp = store?.extra_data?.whatsapp ?? null;
  const isInvoiceOnly = store?.extra_data?.is_invoice_only ?? null;
  const [itemAssets, setItemAssets] = useState([]);
  const description = item?.description
    ? stripHtml(item.description, { ignoreTags: ["br"] })?.result
    : "";

  useEffect(() => {
    let assets;
    if (item && item.assets && Array.isArray(item.assets)) {
      assets = item?.assets
        .sort((a, b) => a.position - b.position)
        .map((asset, key) => {
          if (IS_THUMBNAILER_ENABELED === "true") {
            return (
              <SwiperSlide key={key}>
                <img
                  src={`${THUMBNAILER_URL}?src=${asset.asset}&width=640&height=960&upscale=false&extension=jpg`}
                  alt={item.name}
                />
              </SwiperSlide>
            );
          }
          return (
            <SwiperSlide key={key}>
              <img src={asset.asset} alt={item.name} />
            </SwiperSlide>
          );
        });
      setItemAssets([...itemAssets, ...assets]);
    }
  }, []);

  return (
    <Layout containerClass="page-container__product">
      <OGMetaDataHead
        store={store}
        origin={origin}
        product={item}
        page={"product"}
      />
      <div className="relative">
        <div className="max-w-5xl mx-auto md:py-8 flex flex-col md:flex-row h-full">
          <div className="flex-1 relative md:w-1/2 md:p-4 bg-white">
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true }}
              onSlideChange={() => console.log("slide change")}
              onSwiper={(swiper) => console.log(swiper)}
            >
              {itemAssets}
            </Swiper>
          </div>
          <div className="flex-1 p-4 relative bg-white text-gray-900">
            <span className="text-tiny text-secondary hidden">
              {trans("The Summer Edit")}
            </span>
            <h2 className="text-3xl leading-none text-secondary">
              {trans(item.brand, { [item.brand]: item.brand })}
            </h2>
            <h3 className="text-xl leading-tight pt-4 py-2">
              {trans(item.name, { [item.name]: item.name })}
            </h3>
            <h4 className="text-xl leading-none">
              {item?.price?.toFixed(0)} {item.price_currency?.code}
            </h4>
            <div className="flex my-6">
              {whatsapp ? (
                <React.Fragment>
                  <a
                    id="cta-chat-top-shop"
                    className="block rounded border-2 border-secondary hover:border-primary bg-secondary hover:bg-primary text-lg text-white text-center uppercase p-3 w-full transition-colors"
                    href={`https://api.whatsapp.com/send?phone=${whatsapp}&text=Hello, I’m interested in the ${encodeURIComponent(
                      item.name
                    )}: ${origin}/${locale}/products/${item.slug}`}
                    title={`Chat with ${store.name} about ${item.name} on WhatsApp`}
                    target="_blank"
                    data-gtm-event="chat-to-shop"
                    data-gtm-product={item?.id}
                  >
                    {trans("chat to shop")}
                  </a>
                  <span className="w-6 h-4 inline-block"></span>
                </React.Fragment>
              ) : null}
              {isInvoiceOnly ? (
                item?.external_url ? (
                  <a
                    id="cta-buy-now"
                    className="block rounded border-2 border-secondary hover:border-primary text-lg text-secondary hover:text-primary text-center uppercase p-3 w-full transition-colors"
                    href={item.external_url}
                    title={`Buy ${item.name} now`}
                    target="_blank"
                    data-gtm-event="buy-now-external"
                    data-gtm-product={item?.id}
                  >
                    {trans("buy now")}
                  </a>
                ) : null
              ) : (
                <a
                  id="cta-buy-now"
                  className="block rounded border-2 border-secondary hover:border-primary text-lg text-secondary hover:text-primary text-center uppercase p-3 w-full transition-colors"
                  href={item.checkout_url}
                  title={`Buy ${item.name} now`}
                  target="_blank"
                  data-gtm-event="buy-now"
                  data-gtm-product={item?.id}
                >
                  {trans("buy now")}
                </a>
              )}
            </div>
            <h4 className="font-bold text-tiny leading-none mb-2">
              {trans("Product details")}
            </h4>
            <div
              className="text-sm multi-paragraph-text-container"
              dangerouslySetInnerHTML={{
                __html: trans(description, { description }),
              }}
            ></div>
            <span className="block text-sm pt-4">
              {trans(
                "Shop with confidence with our Contactless Delivery Service, and pay online using your Visa or Mastercard."
              )}
            </span>
            <span className="block text-sm pt-4">
              <Trans>
                By clicking "Chat to Shop" you agree to our{" "}
                <a
                  href={`https://zbooni.com/terms`}
                  target="_blank"
                  className="underline"
                >
                  terms and conditions
                </a>{" "}
                and{" "}
                <a
                  href={`https://www.zbooni.com/privacy`}
                  target="_blank"
                  className="underline"
                >
                  privacy policy.
                </a>
              </Trans>
            </span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export const getServerSideProps = async ({
  params: { slug },
  req,
  res,
  locale,
}) => {
  const { data } = await AuthService.getToken();
  setDefaultAuthHeader(data.access_token);
  setUserLang(locale);
  const { origin } = absoluteUrl(req);
  const subdomain = generateSubdomainFromLocale(locale);
  const [storeApiError, storeApiResponse] = await asyncAwaitWrap(
    StoreService.getStoreBySubdomain(subdomain)
  );
  if (storeApiError || !storeApiResponse.data.count) {
    return {
      notFound: true,
    };
  }
  const { data: storeObject } = storeApiResponse;
  const url = `${storeObject.results?.[0]?.products}`;
  const productApiResponse = await StoreService.getProductBySlug(url, slug);
  const { data: item } = productApiResponse;

  if (!item.results?.length) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      origin,
      store: storeObject.results?.[0],
      item: item.results?.[0],
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
};

export default Product;
